import React, {useEffect, useState} from 'react';
import {loadData} from "./actions/mainPageActions";
import {connect} from "react-redux";

function ControlPanel({loadData, regulationsAccepted}) {
    const [selectedTime, setSelectedTime] = useState('week');
    const [isActive, setIsActive] = useState(true);
    const [startDate, setStartDate] = useState(`${new Date().getFullYear()}-${new Date().getMonth()+1}-${new Date().getDate()-6}`);
    const [endDate, setEndDate] = useState(`${new Date().getFullYear()}-${new Date().getMonth()+1}-${new Date().getDate()}`);
    const [showCalendar, setShowCalendar] = useState(false);
    const [countOfChangesInCalendar, setCountOfChangesInCalendar] = useState(0);
    const [hasSimulations, setHasSimulations] = useState(false);

    useEffect(() => {
        setHasSimulations(window.location.pathname.includes('simulations/'));
    }, []);

    useEffect(() => {
        if(regulationsAccepted || hasSimulations) {
            loadData({page: 0, dateRange: {start: startDate, end: endDate}, active: isActive? "active" : "notActive"})
        }
    }, [isActive, countOfChangesInCalendar, regulationsAccepted]);


    // Funkcja do formatowania daty
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Funkcja do ustawiania zakresu dat
    const setDateRange = (timeRange) => {
        const today = new Date();
        let start, end;

        switch(timeRange) {
            case 'week':
                start = new Date(today);
                start.setDate(today.getDate() - 6);
                end = new Date(today);
                break;
            case 'month':
                start = new Date(today);
                start.setDate(today.getDate() + 1 );
                start.setMonth(today.getMonth() - 1 );
                end = new Date(today);
                break;
            case 'year':
                start = new Date(today.getFullYear() - 1, 0, 2); // Pierwszy dzień poprzedniego roku
                end = new Date(today.getFullYear(), 0, 1); // Ostatni dzień poprzedniego roku
                break;
            default:
                return;
        }

        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
        setCountOfChangesInCalendar(countOfChangesInCalendar + 1)
    };

    // Obsługa zmiany zakresu czasowego
    const handleTimeRangeChange = (timeRange) => {
        setSelectedTime(timeRange);
        setDateRange(timeRange);
        setCountOfChangesInCalendar(countOfChangesInCalendar + 1)
    };

    // Funkcja sprawdzająca czy daty odpowiadają predefiniowanemu zakresowi
    const checkIfDatesMatchPredefined = (start, end) => {
        const today = new Date();

        // Sprawdź Last Week (ostatnie 7 dni)
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        const weekEnd = new Date(today);

        if (start === weekStart.toISOString().split('T')[0] &&
            end === weekEnd.toISOString().split('T')[0]) {
            return 'week';
        }

        // Sprawdź Last Month (ostatni miesiąc)
        const monthStart = new Date(today);
        monthStart.setMonth(today.getMonth() - 1);
        const monthEnd = new Date(today);

        if (start === monthStart.toISOString().split('T')[0] &&
            end === monthEnd.toISOString().split('T')[0]) {
            return 'month';
        }

        // Sprawdź Last Year (poprzedni pełny rok)
        const yearStart = new Date(today.getFullYear() - 1, 0, 1);
        const yearEnd = new Date(today.getFullYear() - 1, 11, 31);

        if (start === yearStart.toISOString().split('T')[0] &&
            end === yearEnd.toISOString().split('T')[0]) {
            return 'year';
        }

        return null; // Nie pasuje do żadnego predefiniowanego zakresu
    };

    // Obsługa zmiany dat w inputach
    const handleDateChange = (newStartDate, newEndDate) => {
        const matchedRange = checkIfDatesMatchPredefined(newStartDate || startDate, newEndDate || endDate);
        setSelectedTime(matchedRange || ''); // Odznacz wszystko jeśli nie pasuje
    };

    // Obsługa apply dla kalendarza
    const handleApplyDates = () => {
        const matchedRange = checkIfDatesMatchPredefined(startDate, endDate);
        setSelectedTime(matchedRange || ''); // Odznacz wszystko jeśli nie pasuje do predefiniowanych
        setShowCalendar(false);
        // Tutaj można dodać callback do rodzica z nowymi datami
        setCountOfChangesInCalendar(countOfChangesInCalendar + 1)
    };

    const styles = {
        container: {
            minHeight: '350px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '24px',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            position: 'relative'
        },
        title: {
            fontSize: '18px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            marginBottom: '4px'
        },
        subtitle: {
            fontSize: '13px',
            color: 'rgb(123, 128, 154)',
            marginBottom: '20px',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            fontWeight: '300'
        },
        statusToggle: {
            display: 'flex',
            gap: '1rem',
            marginBottom: '24px',
            padding: '4px 12px',
        },
        statusButton: (active) => ({
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '13px',
            backgroundColor: active ? '#CFF4D2' : 'transparent',
            color: 'rgb(52, 71, 103)',
            border: 'none',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'background-color 0.2s ease'
        }),
        timeRange: {
            display: 'flex',
            gap: '1rem',
            marginBottom: '20px',
            padding: '4px 12px',
        },
        timeButton: (active) => ({
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '13px',
            backgroundColor: active ? '#CFF4D2' : 'transparent',
            color: 'rgb(52, 71, 103)',
            border: 'none',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'background-color 0.2s ease'
        }),
        dateRange: {
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
            color: 'rgb(52, 71, 103)',
            gap: '8px',
            marginBottom: '36px',
            fontWeight: '500',
            paddingLeft: 24,
            cursor: 'pointer',
            padding: '8px 24px',
            borderRadius: '6px',
            transition: 'background-color 0.2s ease',
        },
        dateRangeHover: {
            backgroundColor: 'rgba(207, 244, 210, 0.3)'
        },
        calendarIcon: {
            width: '16px',
            height: '16px'
        },
        separator: {
            height: '1px',
            width: '100%',
            backgroundImage: 'linear-gradient(to right, rgba(23, 23, 23, 0), rgba(23, 23, 23, 0.1), rgba(23, 23, 23, 0))',
            marginTop: '12px'
        },
        calendarPopup: {
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            padding: '20px',
            zIndex: 1000,
            border: '1px solid rgba(0, 0, 0, 0.1)',
            marginTop: '8px'
        },
        calendarGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px'
        },
        dateInput: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
        },
        dateLabel: {
            fontSize: '12px',
            color: 'rgb(123, 128, 154)',
            fontWeight: '500'
        },
        dateInputField: {
            padding: '8px 12px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '6px',
            fontSize: '13px',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        calendarButtons: {
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end'
        },
        calendarButton: (primary = false) => ({
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '13px',
            backgroundColor: primary ? '#CFF4D2' : 'transparent',
            color: 'rgb(52, 71, 103)',
            border: primary ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease'
        })
    };

    return (
        <div style={styles.container}>
            <div style={styles.title}>Control panel</div>
            <div style={styles.subtitle}>Select activity and time range</div>

            {/* Status Toggle */}
            <div style={styles.statusToggle}>
                <button
                    style={styles.statusButton(isActive)}
                    onClick={() => setIsActive(true)}
                >
                    Active
                </button>
                <button
                    style={styles.statusButton(!isActive)}
                    onClick={() => setIsActive(false)}
                >
                    Not active
                </button>
            </div>

            {/* Time Range Toggle */}
            <div style={styles.timeRange}>
                <button
                    style={styles.timeButton(selectedTime === 'week')}
                    onClick={() => handleTimeRangeChange('week')}
                >
                    Last week
                </button>
                <button
                    style={styles.timeButton(selectedTime === 'month')}
                    onClick={() => handleTimeRangeChange('month')}
                >
                    Last month
                </button>
                <button
                    style={styles.timeButton(selectedTime === 'year')}
                    onClick={() => handleTimeRangeChange('year')}
                >
                    Last year
                </button>
                {selectedTime === '' && (
                    <span style={{
                        fontSize: '12px',
                        color: 'rgb(123, 128, 154)',
                        fontStyle: 'italic',
                        alignSelf: 'center',
                        marginLeft: '12px'
                    }}>Custom date range</span>
                )}
            </div>

            {/* Date Range Display */}
            <div
                style={styles.dateRange}
                onClick={() => setShowCalendar(!showCalendar)}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(207, 244, 210, 0.3)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
                <svg style={styles.calendarIcon} fill="none" stroke="#999" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M8 7V3M16 7V3M3 11H21M5 5H19C20.1046 5 21 5.89543 21 7V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7C3 5.89543 3.89543 5 5 5Z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {formatDate(startDate)} - {formatDate(endDate)}
            </div>

            {/* Calendar Popup */}
            {showCalendar && (
                <div style={styles.calendarPopup}>
                    <div style={styles.calendarGrid}>
                        <div style={styles.dateInput}>
                            <label style={styles.dateLabel}>Start date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                    handleDateChange(e.target.value, endDate);
                                }}
                                style={styles.dateInputField}
                            />
                        </div>
                        <div style={styles.dateInput}>
                            <label style={styles.dateLabel}>End date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    handleDateChange(startDate, e.target.value);
                                }}
                                style={styles.dateInputField}
                            />
                        </div>
                    </div>
                    <div style={styles.calendarButtons}>
                        <button
                            style={styles.calendarButton(false)}
                            onClick={() => setShowCalendar(false)}
                        >
                            Cancel
                        </button>
                        <button
                            style={styles.calendarButton(true)}
                            onClick={handleApplyDates}
                        >
                            Accept
                        </button>
                    </div>
                </div>
            )}

            <div style={styles.separator}></div>
        </div>
    );
}
const mapStateToProps = ({ clientPanel }) => {
    return {
        regulationsAccepted: clientPanel.clientPanels.regulationsAccepted
    }
}

function mapDispatchToProps () {
    return dispatch => ({
        loadData: (customFilters) => dispatch(loadData(customFilters))
    });
}
export default connect(mapStateToProps, mapDispatchToProps)(ControlPanel);